import { useEffect, useMemo } from 'react';
import { type Options } from '@grnhse/seedling/lib/birch/components/forms/dropdown/use_single_dropdown';
import {
  type Item,
  useSingleDropdown,
} from '@grnhse/seedling/lib/birch/components/forms/dropdown';

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

// The dropdown loader sometimes tries to load options without a search term or when an item
// is already selected. For our autocomplete, we only want to load options if the user has entered
// something into the input field and has not already selected an option. This hook wraps the
// incoming options to prevent loading when the term is blank, or we already have a selected item.
function useAutocompleteOptions<T>(options: Options<T>) {
  return useMemo(() => {
    const comboboxOptions = { ...options };

    if (options.fetchItems) {
      comboboxOptions.fetchItems = async (term: string, nextPage: number) => {
        if (!term) {
          return {
            items: [] as Item<T>[],
          } as unknown as { items: Item<T>[]; more: boolean };
        }

        return options.fetchItems(term, nextPage) as unknown as {
          items: Item<T>[];
          more: boolean;
        };
      };
    }

    return comboboxOptions;
  }, [options]);
}

export function useAutocompleteDropdown<T>(options: Options<T>) {
  const comboboxOptions = useAutocompleteOptions(options);
  const dropdownProps = useSingleDropdown(comboboxOptions);

  useEffect(() => {
    if (!dropdownProps.inputValue && dropdownProps.isOpen) {
      dropdownProps.closeMenu();
    }
  }, [dropdownProps]);

  return useMemo(() => {
    return {
      ...dropdownProps,
      loadInitialOptions: noop,
      openMenu: () => {
        if (
          !dropdownProps.inputValue ||
          (dropdownProps.selectedItem &&
            dropdownProps.selectedItem.label === dropdownProps.inputValue)
        ) {
          return;
        }

        dropdownProps.openMenu();
      },
      preventLoadOnMount: true,
      showToggle: false,
      toggleMenu: noop,
    };
  }, [dropdownProps]);
}
